import type { TState } from "@/types/issue-metadata";

export type TStateGroup = TState["group"];

// Order matches the WorkItemStateGroup enum (backend state.py) so Settings, the
// board, and the status menus all read left-to-right / top-to-bottom as one
// workflow. `on_hold` is a real, seeded group (#3179): omitting it here hid it
// from Settings while the flat status menus still showed it, so the two
// disagreed and the On Hold column silently vanished from the board.
export const STATE_GROUPS: readonly TStateGroup[] = [
  "backlog",
  "unstarted",
  "started",
  "on_hold",
  "completed",
  "cancelled",
] as const;

export const STATE_GROUP_LABEL_KEYS: Record<TStateGroup, string> = {
  backlog: "workspace_settings.states.group.backlog",
  unstarted: "workspace_settings.states.group.unstarted",
  started: "workspace_settings.states.group.started",
  on_hold: "workspace_settings.states.group.on_hold",
  completed: "workspace_settings.states.group.completed",
  cancelled: "workspace_settings.states.group.cancelled",
};

/** The `ui/badge.tsx` variants a state group maps onto. Named so a caller can
 *  type its own map without importing Badge's internals. */
export type StateGroupBadgeVariant =
  | "status-neutral"
  | "status-info"
  | "status-warning"
  | "status-success"
  | "destructive";

// One state-group → Badge-tone mapping for the whole app, so a status label gets
// the same tone wherever it is rendered. Lives here, beside the group vocabulary
// itself, rather than being re-derived per feature (Badge's usage record: don't
// add another ad-hoc status-color map in a feature file).
export const STATE_GROUP_BADGE_VARIANTS: Record<TStateGroup, StateGroupBadgeVariant> = {
  backlog: "status-neutral",
  unstarted: "status-neutral",
  started: "status-info",
  on_hold: "status-warning",
  completed: "status-success",
  cancelled: "destructive",
};

/** The Badge tone for a backend state-group code. Unknown / absent codes fall
 *  back to the neutral tone — a status we can't classify still reads as a
 *  status, never as an error. */
export function stateGroupBadgeVariant(code: string | null | undefined): StateGroupBadgeVariant {
  if (!code) return "status-neutral";
  return STATE_GROUP_BADGE_VARIANTS[code as TStateGroup] ?? "status-neutral";
}

/** The groups whose work is finished: nothing is left to schedule, chase or
 *  staff. Mirrors `CLOSED_STATE_GROUPS` in the backend's
 *  `work_item_states/models.py`. */
const CLOSED_STATE_GROUPS: ReadonlySet<string> = new Set<TStateGroup>(["completed", "cancelled"]);

/** Whether a backend state-group code is a closed one. Unknown or absent codes
 *  read as open, so a status we can't classify keeps its open-work treatment. */
export function isClosedStateGroup(code: string | null | undefined): boolean {
  return code != null && CLOSED_STATE_GROUPS.has(code);
}
